import { Card, CardBody, CardTitle, CardText, Badge, Button } from 'reactstrap';
import { Box, Tooltip, IconButton } from '@mui/material';
import { DescriptionOutlined as LeaseIcon, Visibility as PreviewIcon } from '@mui/icons-material';
import FeatherIcon from 'feather-icons-react';
import './leases.scss';

const LeaseStatus = {
    RELEASE_REQUEST: 'Re-lease Request',
    PENDING: 'Pending Approval',
    SCHEDULED: 'Scheduled Lease',
    SIGNED: 'Signed',
    RENEWAL_IN_PROGRESS: 'Renewal in Progress',
    ON_HOLD: 'On Hold',
    WAITING_FOR_SIGNATURES: 'Waiting for signatures',
    ACTIVE: 'Active',
    EARLY_TERMINATION: 'Early Termination',
    TERMINATED: 'Terminated',
    EVICTION_INITIATED: 'Eviction Initiated',
    EXPIRED: 'Expired',
    ARCHIVED: 'Archived',
};

const statusToKey = Object.entries(LeaseStatus).reduce((acc, [key, value]) => {
    acc[value] = key;
    return acc;
}, {});

const getStatusColor = (status) => {
    const statusColors = {
        ACTIVE: { bg: '#e8f5e8', text: '#2e7d32', border: '#4caf50' },
        PENDING: { bg: '#fff3e0', text: '#f57c00', border: '#ff9800' },
        SCHEDULED: { bg: '#e3f2fd', text: '#1976d2', border: '#2196f3' },
        SIGNED: { bg: '#e8f5e8', text: '#388e3c', border: '#4caf50' },
        RENEWAL_IN_PROGRESS: { bg: '#e1f5fe', text: '#0277bd', border: '#03a9f4' },
        ON_HOLD: { bg: '#fafafa', text: '#616161', border: '#9e9e9e' },
        WAITING_FOR_SIGNATURES: { bg: '#fff8e1', text: '#f9a825', border: '#ffc107' },
        EARLY_TERMINATION: { bg: '#ffebee', text: '#d32f2f', border: '#f44336' },
        TERMINATED: { bg: '#ffebee', text: '#c62828', border: '#f44336' },
        EVICTION_INITIATED: { bg: '#fce4ec', text: '#ad1457', border: '#e91e63' },
        EXPIRED: { bg: '#f3e5f5', text: '#7b1fa2', border: '#9c27b0' },
        ARCHIVED: { bg: '#f5f5f5', text: '#757575', border: '#bdbdbd' },
        RELEASE_REQUEST: { bg: '#e0f2f1', text: '#00695c', border: '#009688' },
    };
    const statusKey = statusToKey[status] || status;
    return statusColors[statusKey] || { bg: '#f5f5f5', text: '#757575', border: '#bdbdbd' };
};

const LeaseCard = ({ lease, handlePreviewOpen }) => {
    const statusColor = getStatusColor(lease.lease_status?.state);
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return 'N/A';
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
        return match ? `${match[3]}-${match[2]}-${match[1]}` : 'Invalid Date';
    };

    return (
        <Card
            className="mb-3 shadow-sm border-0 hover-shadow"
            style={{
                borderRadius: '12px',
                transition: 'transform 0.2s, box-shadow 0.2s',
                backgroundColor: statusColor.bg
            }}
        >
            <CardBody className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center">
                        <LeaseIcon style={{ color: statusColor.text, marginRight: '8px' }} />
                        <CardTitle tag="h6" className="mb-0" style={{ color: statusColor.text }}>
                            Lease {lease._id ? lease._id.slice(-6).toUpperCase() : 'N/A'}
                        </CardTitle>
                    </div>
                    <Tooltip title="Preview Lease">
                        <IconButton
                            size="small"
                            onClick={() => handlePreviewOpen(lease._id)}
                            style={{ color: statusColor.text }}
                        >
                            <PreviewIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                </div>
                <hr style={{ borderColor: statusColor.border, opacity: 0.3 }} />
                <CardText className="medium">
                    <div className="mb-1">
                        <FeatherIcon icon="users" size={14} className="me-2" />
                        <span>Tenant(s): {lease.tenants?.map((t) => t.name).join(', ') || 'N/A'}</span>
                    </div>
                    <div className="mb-1">
                        <FeatherIcon icon="calendar" size={14} className="me-2" />
                        <span>Start: {lease.lease_details?.start_date
                            ? formatDateForDisplay(lease.lease_details?.start_date)
                            : 'N/A'}</span>
                    </div>
                    <div className="mb-1">
                        <FeatherIcon icon="calendar" size={14} className="me-2" />
                        <span>End:
                            {lease.lease_details?.end_date
                                ? formatDateForDisplay(lease.lease_details?.end_date)
                                : 'N/A'}</span>
                    </div>
                    <div>
                        <FeatherIcon icon="info" size={14} className="me-2" />
                        <Badge
                            color="light"
                            style={{
                                backgroundColor: statusColor.border,
                                color: 'white',
                                fontWeight: '500'
                            }}
                        >
                            {lease.lease_status?.state
                                ? (LeaseStatus[lease.lease_status.state] || lease.lease_status.state)
                                : 'N/A'}
                        </Badge>
                    </div>
                </CardText>
            </CardBody>
        </Card>
    );
};

const LeasesGrid = ({ leases, handlePreviewOpen }) => {
    return (
        <div className="row mt-2">
            {leases.map((lease) => (
                <div className="col-12 col-sm-6 col-md-4" key={lease._id}>
                    <LeaseCard lease={lease} handlePreviewOpen={handlePreviewOpen} />
                </div>
            ))}
        </div>
    );
};

export { LeasesGrid };